import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithCrashReporting } from './crashReportingBaseQuery';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithCrashReporting,
  tagTypes: ['Listings', 'SavedListings', 'Enquiries', 'EnquiriesV2', 'Offers', 'Quotes', 'Bookings', 'Stays', 'SupportCases', 'Rewards', 'Organizations', 'Config', 'User', 'Reviews', 'ExternalReviews', 'PartnerPolicy', 'PartnerChat', 'PartnerInvoices', 'PropertyPolicies', 'NotificationConfig', 'NotificationTemplates', 'EmailDeliveries', 'EnquiryContactLogs', 'WhatsAppInbound', 'AdminInvitations', 'Administrators'],
  endpoints: (builder) => ({

    // ── Auth (v1 — keep as-is) ─────────────────────────────────────────────
    register: builder.mutation({
      query: (credentials) => ({ url: '/v2/auth/register', method: 'POST', body: credentials }),
    }),
    completePartnerOnboarding: builder.mutation({
      query: () => ({ url: '/v2/partners/onboarding/complete', method: 'POST' }),
    }),
    login: builder.mutation({
      query: (credentials) => ({ url: '/v2/auth/login', method: 'POST', body: credentials }),
    }),
    getMe: builder.query({
      query: () => '/v2/auth/me',
      providesTags: ['User'],
    }),
    updateMe: builder.mutation({
      query: (body) => ({ url: '/v2/auth/me', method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),
    createPublicContact: builder.mutation({
      query: (body) => ({ url: '/v2/public/contact', method: 'POST', body }),
    }),
    verifyAdminInvitation: builder.mutation({ query: (body) => ({ url: '/v2/auth/admin-invitations/verify', method: 'POST', body }) }),
    completeAdminInvitation: builder.mutation({ query: (body) => ({ url: '/v2/auth/admin-invitations/complete', method: 'POST', body }) }),
    getAdminInvitations: builder.query({ query: () => '/v2/admin/invitations', providesTags: ['AdminInvitations'] }),
    createAdminInvitation: builder.mutation({ query: (body) => ({ url: '/v2/admin/invitations', method: 'POST', body }), invalidatesTags: ['AdminInvitations'] }),
    revokeAdminInvitation: builder.mutation({ query: (id) => ({ url: `/v2/admin/invitations/${id}`, method: 'DELETE' }), invalidatesTags: ['AdminInvitations'] }),
    getAdministrators: builder.query({ query: () => '/v2/admin/invitations/admins', providesTags: ['Administrators'] }),
    updateAdministratorAccess: builder.mutation({
      query: ({ id, sections }) => ({ url: `/v2/admin/invitations/admins/${id}/access`, method: 'PATCH', body: { sections } }),
      invalidatesTags: ['Administrators'],
    }),
    revokeAdministratorAccess: builder.mutation({
      query: (id) => ({ url: `/v2/admin/invitations/admins/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Administrators'],
    }),

    // ── Listings (v1 — keep as-is) ────────────────────────────────────────
    getListings: builder.query({
      query: (params) => {
        const sp = new URLSearchParams();
        Object.entries(params || {}).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') {
            Array.isArray(v) ? v.forEach(x => sp.append(k, x)) : sp.append(k, v);
          }
        });
        return `/v2/listings?${sp.toString()}`;
      },
      providesTags: ['Listings'],
    }),
    getListingsPreview: builder.query({
      query: (params) => {
        const sp = new URLSearchParams();
        Object.entries(params || {}).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') {
            Array.isArray(v) ? v.forEach(x => sp.append(k, x)) : sp.append(k, v);
          }
        });
        return `/v2/listings/preview?${sp.toString()}`;
      },
      providesTags: ['Listings'],
    }),
    getListing: builder.query({
      query: (id) => `/v2/listings/${id}`,
      providesTags: (result, error, id) => [{ type: 'Listings', id }],
    }),
    getListingAvailability: builder.query({
      query: (id) => `/v2/listings/${id}/availability`,
    }),
    getSavedListings: builder.query({
      query: () => '/v2/saved-listings',
      providesTags: ['SavedListings'],
    }),
    saveListing: builder.mutation({
      query: (listingId) => ({ url: `/v2/saved-listings/${listingId}`, method: 'PUT' }),
      invalidatesTags: ['SavedListings'],
    }),
    removeSavedListing: builder.mutation({
      query: (listingId) => ({ url: `/v2/saved-listings/${listingId}`, method: 'DELETE' }),
      invalidatesTags: ['SavedListings'],
    }),

    // ── Enquiries v1 (legacy — keep) ──────────────────────────────────────
    createEnquiry: builder.mutation({
      query: (enquiry) => ({ url: '/v2/enquiries', method: 'POST', body: enquiry }),
      invalidatesTags: ['Enquiries'],
    }),
    getUserEnquiries: builder.query({
      // userId scopes RTK Query's cache across account and impersonation changes.
      query: (_userId) => '/v2/enquiries',
      providesTags: ['Enquiries'],
    }),
    getEnquiry: builder.query({
      query: (id) => `/v2/enquiries/${id}`,
      providesTags: (result, error, id) => [{ type: 'Enquiries', id }],
    }),
    submitBookingIdConfirmation: builder.mutation({
      query: ({ enquiryId, bookingId }) => ({
        url: '/v2/booking-confirmation/user/submit-booking-id',
        method: 'POST',
        body: { enquiry_id: enquiryId, booking_id: bookingId },
      }),
      invalidatesTags: ['Enquiries'],
    }),
    submitBookingIdVerification: builder.mutation({
      query: ({ enquiryId, bookingId }) => ({
        url: `/v2/booking-verification/user/submit-booking-id?enquiry_id=${enquiryId}&booking_id=${bookingId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Enquiries'],
    }),
    partnerSubmitBookingId: builder.mutation({
      query: ({ enquiryId, bookingId }) => ({
        url: '/v2/booking-confirmation/partner/submit-booking-id',
        method: 'POST',
        body: { enquiry_id: enquiryId, booking_id: bookingId },
      }),
      invalidatesTags: ['Enquiries'],
    }),
    adminConfirmBookingManually: builder.mutation({
      query: ({ enquiryId, lockedPrice, confirmationMethod, confirmationNotes }) => ({
        url: '/v2/booking-confirmation/admin/confirm-booking-manually',
        method: 'POST',
        body: { enquiry_id: enquiryId, locked_price: lockedPrice, confirmation_method: confirmationMethod, confirmation_notes: confirmationNotes },
      }),
      invalidatesTags: ['Enquiries'],
    }),

    // ── Config v2 ─────────────────────────────────────────────────────────
    getExchangeRates: builder.query({
      query: () => '/v2/config/exchange-rates',
      providesTags: ['Config'],
    }),
    refreshExchangeRates: builder.mutation({
      query: () => ({ url: '/v2/config/exchange-rates/refresh', method: 'POST' }),
      invalidatesTags: ['Config'],
    }),

    // ── Organizations v2 ──────────────────────────────────────────────────
    getMyOrganization: builder.query({
      query: () => '/v2/organizations/me',
      providesTags: ['Organizations'],
    }),
    createOrganization: builder.mutation({
      query: (data) => ({ url: '/v2/organizations/me', method: 'POST', body: data }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: builder.mutation({
      query: (data) => ({ url: '/v2/organizations/me', method: 'PATCH', body: data }),
      invalidatesTags: ['Organizations'],
    }),
    listOrganizations: builder.query({
      query: () => '/v2/organizations/',
      providesTags: ['Organizations'],
    }),

    // ── Enquiries v2 ──────────────────────────────────────────────────────
    createEnquiryV2: builder.mutation({
      query: (data) => ({ url: '/v2/enquiries', method: 'POST', body: data }),
      invalidatesTags: ['EnquiriesV2'],
    }),
    getMyEnquiriesV2: builder.query({
      query: () => '/v2/enquiries/me',
      providesTags: ['EnquiriesV2'],
    }),
    getEnquiryV2: builder.query({
      query: (id) => `/v2/enquiries/${id}`,
      providesTags: (result, error, id) => [{ type: 'EnquiriesV2', id }],
    }),
    getEnquiryWorkflow: builder.query({
      // scope is a non-secret actor/role key: never reuse admin data in a partner session.
      query: ({ enquiryId }) => `/v2/enquiries/${enquiryId}/workflow`,
      keepUnusedDataFor: 0,
      providesTags: ['EnquiriesV2', 'Offers', 'Quotes', 'Bookings', 'Enquiries'],
    }),
    cancelEnquiryV2: builder.mutation({
      query: (id) => ({ url: `/v2/enquiries/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['EnquiriesV2'],
    }),
    listAllEnquiriesV2: builder.query({
      query: () => '/v2/enquiries',
      providesTags: ['EnquiriesV2'],
    }),
    qualifyEnquiry: builder.mutation({
      query: (id) => ({ url: `/v2/enquiries/${id}/qualify`, method: 'POST' }),
      invalidatesTags: ['EnquiriesV2'],
    }),
    matchEnquiry: builder.mutation({
      query: ({ id, listing_ids }) => ({ url: `/v2/enquiries/${id}/match`, method: 'POST', body: { listing_ids } }),
      invalidatesTags: ['EnquiriesV2'],
    }),

    // ── Offers v2 ─────────────────────────────────────────────────────────
    getMyOfferRequests: builder.query({
      query: () => '/v2/partner/offers/requests',
      providesTags: ['Offers'],
    }),
    confirmOffer: builder.mutation({
      query: ({ offerId, ...data }) => ({ url: `/v2/partner/offers/${offerId}/confirm`, method: 'POST', body: data }),
      invalidatesTags: ['Offers'],
    }),
    declineOffer: builder.mutation({
      query: ({ offerId, reason }) => ({ url: `/v2/partner/offers/${offerId}/decline`, method: 'POST', body: { reason } }),
      invalidatesTags: ['Offers'],
    }),
    createOfferForEnquiry: builder.mutation({
      query: ({ enquiryId, ...data }) => ({ url: `/v2/admin/enquiries/${enquiryId}/create-offer`, method: 'POST', body: data }),
      invalidatesTags: ['Offers', 'EnquiriesV2'],
    }),

    // ── Quotes v2 ─────────────────────────────────────────────────────────
    createQuote: builder.mutation({
      query: ({ offerId, ...data }) => ({ url: `/v2/admin/offers/${offerId}/quotes`, method: 'POST', body: data }),
      invalidatesTags: ['Quotes'],
    }),
    sendQuote: builder.mutation({
      query: (quoteId) => ({ url: `/v2/admin/quotes/${quoteId}/send`, method: 'POST' }),
      invalidatesTags: ['Quotes'],
    }),
    getQuote: builder.query({
      query: (id) => `/v2/quotes/${id}`,
      providesTags: (result, error, id) => [{ type: 'Quotes', id }],
    }),
    getMyQuotes: builder.query({
      query: () => '/v2/quotes/me/list',
      providesTags: ['Quotes'],
    }),
    acceptQuote: builder.mutation({
      query: (quoteId) => ({ url: `/v2/quotes/${quoteId}/accept`, method: 'POST' }),
      invalidatesTags: ['Quotes', 'Bookings', 'EnquiriesV2'],
    }),
    declineQuote: builder.mutation({
      query: (quoteId) => ({ url: `/v2/quotes/${quoteId}/decline`, method: 'POST' }),
      invalidatesTags: ['Quotes'],
    }),

    // ── Bookings v2 ───────────────────────────────────────────────────────
    getMyBookings: builder.query({
      query: () => '/v2/bookings/me',
      providesTags: ['Bookings'],
    }),
    getBooking: builder.query({
      query: (id) => `/v2/bookings/${id}`,
      providesTags: (result, error, id) => [{ type: 'Bookings', id }],
    }),
    verifyPayment: builder.mutation({
      query: ({ bookingId, ...data }) => ({ url: `/v2/admin/bookings/${bookingId}/verify-payment`, method: 'POST', body: data }),
      invalidatesTags: ['Bookings'],
    }),
    reportPayment: builder.mutation({
      query: ({ bookingId, payment_reference }) => ({
        url: `/v2/bookings/${bookingId}/report-payment`, method: 'POST', body: { payment_reference },
      }),
      invalidatesTags: ['Bookings'],
    }),
    confirmBooking: builder.mutation({
      query: (bookingId) => ({ url: `/v2/admin/bookings/${bookingId}/confirm`, method: 'POST' }),
      invalidatesTags: ['Bookings', 'Stays'],
    }),
    resendBookingEmail: builder.mutation({
      query: (bookingId) => ({ url: `/v2/admin/bookings/${bookingId}/resend-email`, method: 'POST' }),
      invalidatesTags: ['Bookings'],
    }),
    adminListBookings: builder.query({
      query: (emailStatus) => `/v2/admin/bookings${emailStatus ? `?email_status=${emailStatus}` : ''}`,
      providesTags: ['Bookings'],
    }),
    adminListBookingSummaries: builder.query({
      query: () => '/v2/admin/bookings/summary',
      providesTags: ['Bookings'],
    }),
    adminGetBookingDetail: builder.query({
      query: (bookingId) => `/v2/admin/bookings/${bookingId}/detail`,
      providesTags: (result, error, bookingId) => [{ type: 'Bookings', id: bookingId }],
    }),

    // ── Stays v2 ──────────────────────────────────────────────────────────
    getMyStays: builder.query({
      query: () => '/v2/stays/me',
      providesTags: ['Stays'],
    }),
    getStay: builder.query({
      query: (id) => `/v2/stays/${id}`,
      providesTags: (result, error, id) => [{ type: 'Stays', id }],
    }),
    checkIn: builder.mutation({
      query: (stayId) => ({ url: `/v2/partner/stays/${stayId}/check-in`, method: 'POST' }),
      invalidatesTags: ['Stays'],
    }),
    startStay: builder.mutation({
      query: (stayId) => ({ url: `/v2/partner/stays/${stayId}/start`, method: 'POST' }),
      invalidatesTags: ['Stays'],
    }),
    checkOut: builder.mutation({
      query: ({ stayId, early = false }) => ({ url: `/v2/partner/stays/${stayId}/check-out`, method: 'POST', body: { early } }),
      invalidatesTags: ['Stays'],
    }),
    markNoShow: builder.mutation({
      query: (stayId) => ({ url: `/v2/partner/stays/${stayId}/no-show`, method: 'POST' }),
      invalidatesTags: ['Stays'],
    }),
    completeStay: builder.mutation({
      query: (stayId) => ({ url: `/v2/admin/stays/${stayId}/complete`, method: 'POST' }),
      invalidatesTags: ['Stays', 'Rewards'],
    }),
    createSupportCase: builder.mutation({
      query: (body) => ({ url: '/v2/support-cases', method: 'POST', body }),
      invalidatesTags: ['SupportCases'],
    }),
    adminListSupportCases: builder.query({
      query: () => '/v2/admin/support-cases',
      providesTags: ['SupportCases'],
    }),
    adminTransitionSupportCase: builder.mutation({
      query: ({ caseId, status, resolution_notes }) => ({
        url: `/v2/admin/support-cases/${caseId}/transition`, method: 'POST',
        body: { status, ...(resolution_notes ? { resolution_notes } : {}) },
      }),
      invalidatesTags: ['SupportCases'],
    }),
    adminListStays: builder.query({
      query: () => '/v2/admin/stays',
      providesTags: ['Stays'],
    }),
    adminListStaySummaries: builder.query({
      query: () => '/v2/admin/stays/summary',
      providesTags: ['Stays'],
    }),
    adminGetStayDetail: builder.query({
      query: (stayId) => `/v2/admin/stays/${stayId}/detail`,
      providesTags: (result, error, stayId) => [{ type: 'Stays', id: stayId }],
    }),
    adminListExtensionRequests: builder.query({
      query: ({ status, source } = {}) => ({ url: '/v2/admin/extension-requests', params: { ...(status ? { status } : {}), ...(source ? { source } : {}) } }),
      providesTags: ['Extensions'],
    }),
    adminGetExtensionRequest: builder.query({
      query: (extensionId) => `/v2/admin/extension-requests/${extensionId}`,
      providesTags: (result, error, extensionId) => [{ type: 'Extensions', id: extensionId }],
    }),
    adminGetEligibleExtensionInvitationTargets: builder.query({
      query: (refreshToken) => ({
        url: '/v2/admin/extension-invitations/eligible',
        params: refreshToken ? { refresh: refreshToken } : undefined,
      }),
      keepUnusedDataFor: 0,
    }),
    adminSendExtensionInvitation: builder.mutation({ query: ({ source, targetId }) => ({ url: `/v2/admin/extension-invitations/${source}/${targetId}/send`, method: 'POST' }), invalidatesTags: ['Extensions'] }),
    adminRequestExtensionAvailability: builder.mutation({ query: ({ extensionId, ...data }) => ({ url: `/v2/admin/extension-requests/${extensionId}/request-availability`, method: 'POST', body: data }), invalidatesTags: ['Extensions'] }),
    adminQuoteExtension: builder.mutation({ query: ({ extensionId, ...data }) => ({ url: `/v2/admin/extension-requests/${extensionId}/quote`, method: 'POST', body: data }), invalidatesTags: ['Extensions'] }),
    adminAcceptExtension: builder.mutation({ query: ({ extensionId, ...data }) => ({ url: `/v2/admin/extension-requests/${extensionId}/accept`, method: 'POST', body: data }), invalidatesTags: ['Extensions', 'Bookings', 'Stays'] }),
    adminRejectExtension: builder.mutation({ query: ({ extensionId, ...data }) => ({ url: `/v2/admin/extension-requests/${extensionId}/reject`, method: 'POST', body: data }), invalidatesTags: ['Extensions', 'Stays'] }),
    adminCancelExtension: builder.mutation({ query: ({ extensionId, ...data }) => ({ url: `/v2/admin/extension-requests/${extensionId}/cancel`, method: 'POST', body: data }), invalidatesTags: ['Extensions', 'Stays'] }),
    adminCreateExtensionInvoice: builder.mutation({ query: (extensionId) => ({ url: `/v2/admin/extension-requests/${extensionId}/create-invoice`, method: 'POST' }), invalidatesTags: ['Extensions'] }),

    // ── Rewards v2 ────────────────────────────────────────────────────────
    getMyRewards: builder.query({
      query: () => '/v2/rewards/me',
      providesTags: ['Rewards'],
    }),
    createReward: builder.mutation({
      query: (data) => ({ url: '/v2/admin/rewards/create', method: 'POST', body: data }),
      invalidatesTags: ['Rewards'],
    }),
    approveReward: builder.mutation({
      query: (rewardId) => ({ url: `/v2/admin/rewards/${rewardId}/approve`, method: 'POST' }),
      invalidatesTags: ['Rewards'],
    }),
    rejectReward: builder.mutation({
      query: ({ rewardId, reason }) => ({ url: `/v2/admin/rewards/${rewardId}/reject`, method: 'POST', body: { reason } }),
      invalidatesTags: ['Rewards'],
    }),

    // ── Reviews v2 ───────────────────────────────────────────────────────
    createReview: builder.mutation({
      query: ({ stayId, ...body }) => ({ url: `/v2/stays/${stayId}/reviews`, method: 'POST', body }),
      invalidatesTags: ['Reviews', 'Stays'],
    }),
    getReviewForStay: builder.query({
      query: (stayId) => `/v2/stays/${stayId}/reviews`,
      providesTags: (result, error, stayId) => [{ type: 'Reviews', id: stayId }],
    }),
    listReviewsForListing: builder.query({
      query: (listingId) => `/v2/listings/${listingId}/reviews`,
      providesTags: (result, error, listingId) => [{ type: 'Reviews', id: `listing-${listingId}` }],
    }),
    getExternalReviewsForListing: builder.query({
      query: (listingId) => `/v2/listings/${listingId}/external-reviews`,
      providesTags: (result, error, listingId) => [{ type: 'ExternalReviews', id: `listing-${listingId}` }],
    }),
    getNearbyPlacesForListing: builder.query({
      query: (listingId) => `/v2/listings/${listingId}/nearby-places`,
      providesTags: (result, error, listingId) => [{ type: 'NearbyPlaces', id: `listing-${listingId}` }],
    }),

    // ── Documents v2 ──────────────────────────────────────────────────────
    getUploadUrl: builder.mutation({
      query: (data) => ({ url: '/v2/documents/upload-url', method: 'POST', body: data }),
    }),
    registerDocument: builder.mutation({
      query: (data) => ({ url: '/v2/documents', method: 'POST', body: data }),
    }),
    getDocuments: builder.query({
      query: ({ entityType, entityId } = {}) => {
        const sp = new URLSearchParams();
        if (entityType) sp.append('entity_type', entityType);
        if (entityId) sp.append('entity_id', entityId);
        return `/v2/documents?${sp.toString()}`;
      },
    }),

    // ── Partner Policy ──────────────────────────────────────────────────────
    getMyPolicy: builder.query({
      query: () => '/v2/partner/policy',
      providesTags: ['PartnerPolicy'],
    }),
    saveMyPolicy: builder.mutation({
      query: (data) => ({ url: '/v2/partner/policy', method: 'PUT', body: data }),
      invalidatesTags: ['PartnerPolicy'],
    }),
    submitPolicyForReview: builder.mutation({
      query: () => ({ url: '/v2/partner/policy/submit', method: 'POST' }),
      invalidatesTags: ['PartnerPolicy'],
    }),
    adminGetPartnerPolicy: builder.query({
      query: (partnerId) => `/v2/admin/partners/${partnerId}/policy`,
      providesTags: (r, e, partnerId) => [{ type: 'PartnerPolicy', id: partnerId }],
    }),
    adminApprovePolicy: builder.mutation({
      query: (partnerId) => ({ url: `/v2/admin/partners/${partnerId}/policy/approve`, method: 'POST' }),
      invalidatesTags: (r, e, partnerId) => [{ type: 'PartnerPolicy', id: partnerId }],
    }),
    adminRejectPolicy: builder.mutation({
      query: ({ partnerId, reason }) => ({
        url: `/v2/admin/partners/${partnerId}/policy/reject`,
        method: 'POST', body: { reason },
      }),
      invalidatesTags: (r, e, { partnerId }) => [{ type: 'PartnerPolicy', id: partnerId }],
    }),
    getListingPartnerPolicy: builder.query({
      query: (listingId) => `/v2/listings/${listingId}/partner-policy`,
      providesTags: ['PartnerPolicy'],
    }),

    // ── Partner property policies ───────────────────────────────────────────
    getMyPropertyPolicies: builder.query({
      query: () => '/v2/partner/property-policies',
      providesTags: ['PropertyPolicies'],
    }),
    saveMyPropertyPolicies: builder.mutation({
      query: (data) => ({ url: '/v2/partner/property-policies', method: 'PUT', body: data }),
      invalidatesTags: ['PropertyPolicies'],
    }),
    getPropertyPolicies: builder.query({
      query: (propertyId) => `/v2/partner/properties/${propertyId}/property-policies`,
      providesTags: (result, error, propertyId) => [{ type: 'PropertyPolicies', id: propertyId }],
    }),
    parsePropertyRules: builder.mutation({
      query: ({ propertyId, pastedText }) => ({
        url: propertyId
          ? `/v2/partner/properties/${propertyId}/property-policies/parse`
          : '/v2/partner/policy/parse',
        method: 'POST',
        body: { pasted_text: pastedText },
      }),
    }),
    savePropertyPolicies: builder.mutation({
      query: ({ propertyId, ...data }) => ({
        url: `/v2/partner/properties/${propertyId}/property-policies`, method: 'PUT', body: data,
      }),
      invalidatesTags: (result, error, { propertyId }) => [{ type: 'PropertyPolicies', id: propertyId }],
    }),
    // Public resolved policies for a listing
    getResolvedPolicies: builder.query({
      query: (listingId) => `/v2/listings/${listingId}/resolved-policies`,
      providesTags: (result) => result?.property_id
        ? [{ type: 'PropertyPolicies', id: result.property_id }]
        : ['PropertyPolicies'],
    }),

    // ── Partner Chat ────────────────────────────────────────────────────────
    getPartnerChat: builder.query({
      query: (partnerId) => `/v2/chat/partner/${partnerId}`,
      providesTags: (r, e, partnerId) => [{ type: 'PartnerChat', id: partnerId }],
    }),
    sendChatMessage: builder.mutation({
      query: ({ partnerId, text }) => ({
        url: `/v2/chat/partner/${partnerId}/message`,
        method: 'POST', body: { text },
      }),
      invalidatesTags: (r, e, { partnerId }) => [{ type: 'PartnerChat', id: partnerId }],
    }),
    markChatRead: builder.mutation({
      query: (partnerId) => ({ url: `/v2/chat/partner/${partnerId}/read`, method: 'PATCH' }),
      invalidatesTags: (r, e, partnerId) => [{ type: 'PartnerChat', id: partnerId }],
    }),
    clearChat: builder.mutation({
      query: (partnerId) => ({ url: `/v2/chat/partner/${partnerId}/clear`, method: 'DELETE' }),
      invalidatesTags: (r, e, partnerId) => [{ type: 'PartnerChat', id: partnerId }],
    }),
    editChatMessage: builder.mutation({
      query: ({ partnerId, messageId, text }) => ({
        url: `/v2/chat/partner/${partnerId}/message/${messageId}`,
        method: 'PATCH', body: { text },
      }),
      invalidatesTags: (r, e, { partnerId }) => [{ type: 'PartnerChat', id: partnerId }],
    }),

    // ── Partner Invoices ────────────────────────────────────────────────────
    getPartnerInvoices: builder.query({
      query: () => '/v2/partner/invoices',
      providesTags: ['PartnerInvoices'],
    }),

    // ── Notification Config (Admin) ──────────────────────────────────────────
    getNotificationConfig: builder.query({
      query: () => '/v2/admin/notification-config',
      providesTags: ['NotificationConfig'],
    }),
    updateNotificationConfig: builder.mutation({
      query: ({ event_key, email_enabled, inapp_enabled, manual_enabled }) => ({
        url: `/v2/admin/notification-config/${event_key}`,
        method: 'PUT',
        body: {
          ...(email_enabled !== undefined ? { email_enabled } : {}),
          ...(inapp_enabled !== undefined ? { inapp_enabled } : {}),
          ...(manual_enabled !== undefined ? { manual_enabled } : {}),
        },
      }),
      invalidatesTags: ['NotificationConfig'],
    }),
    getManualNotificationRecipients: builder.query({
      query: ({ event_key, search = '' }) => ({
        url: '/v2/admin/notification-config/manual/recipients',
        params: { event_key, ...(search ? { search } : {}) },
      }),
    }),
    sendManualNotification: builder.mutation({
      query: (body) => ({
        url: '/v2/admin/notification-config/manual/send',
        method: 'POST',
        body,
      }),
    }),
    getNotificationTemplates: builder.query({
      query: () => '/v2/admin/notification-config/templates',
      providesTags: ['NotificationTemplates'],
    }),
    getNotificationTemplate: builder.query({
      query: (eventKey) => `/v2/admin/notification-config/templates/${eventKey}`,
      providesTags: (result, error, eventKey) => [{ type: 'NotificationTemplates', id: eventKey }],
    }),
    createCustomNotificationTemplateEvent: builder.mutation({
      query: (body) => ({ url: '/v2/admin/notification-config/templates/custom', method: 'POST', body }),
      invalidatesTags: ['NotificationConfig', 'NotificationTemplates'],
    }),
    saveNotificationTemplate: builder.mutation({
      query: ({ eventKey, ...body }) => ({ url: `/v2/admin/notification-config/templates/${eventKey}`, method: 'PUT', body }),
      invalidatesTags: (result, error, { eventKey }) => ['NotificationTemplates', { type: 'NotificationTemplates', id: eventKey }],
    }),
    previewNotificationTemplate: builder.mutation({
      query: ({ eventKey, ...body }) => ({ url: `/v2/admin/notification-config/templates/${eventKey}/preview`, method: 'POST', body }),
    }),
    testNotificationTemplate: builder.mutation({
      query: ({ eventKey, ...body }) => ({ url: `/v2/admin/notification-config/templates/${eventKey}/test`, method: 'POST', body }),
    }),
    getNotificationTemplateHistory: builder.query({
      query: (eventKey) => `/v2/admin/notification-config/templates/${eventKey}/history`,
      providesTags: (result, error, eventKey) => [{ type: 'NotificationTemplates', id: eventKey }],
    }),
    getEmailProviderStatus: builder.query({ query: () => '/v2/admin/notification-config/provider/status' }),
    testEmailProvider: builder.mutation({
      query: (body) => ({ url: '/v2/admin/notification-config/provider/test', method: 'POST', body }),
    }),
    getEmailDeliveries: builder.query({
      query: (status = '') => ({ url: '/v2/admin/notification-config/deliveries', params: status ? { status } : {} }),
      providesTags: ['EmailDeliveries'],
    }),
    retryEmailDelivery: builder.mutation({
      query: (deliveryId) => ({ url: `/v2/admin/notification-config/deliveries/${deliveryId}/retry`, method: 'POST' }),
      invalidatesTags: ['EmailDeliveries'],
    }),

    // ── Admin enquiry intake / WhatsApp ──────────────────────────────────────
    adminCreateEnquiry: builder.mutation({
      query: (body) => ({ url: '/v2/admin/enquiries', method: 'POST', body }),
      invalidatesTags: ['Enquiries'],
    }),
    adminLookupEnquiriesByPhone: builder.query({
      query: (phone) => ({ url: '/v2/admin/enquiries/lookup', params: { phone } }),
      keepUnusedDataFor: 0,
    }),
    getEnquiryContactLogs: builder.query({
      query: (enquiryId) => `/v2/admin/enquiries/${enquiryId}/contact-logs`,
      providesTags: (r, e, enquiryId) => [{ type: 'EnquiryContactLogs', id: enquiryId }],
    }),
    addEnquiryContactLog: builder.mutation({
      query: ({ enquiryId, ...body }) => ({ url: `/v2/admin/enquiries/${enquiryId}/contact-logs`, method: 'POST', body }),
      invalidatesTags: (r, e, { enquiryId }) => [{ type: 'EnquiryContactLogs', id: enquiryId }, 'Enquiries'],
    }),
    getWhatsAppStatus: builder.query({
      query: () => '/v2/whatsapp/status',
    }),
    sendWhatsAppTemplate: builder.mutation({
      query: ({ enquiryId, ...body }) => ({ url: `/v2/whatsapp/enquiries/${enquiryId}/send-template`, method: 'POST', body }),
      invalidatesTags: (r, e, { enquiryId }) => [{ type: 'EnquiryContactLogs', id: enquiryId }, 'Enquiries'],
    }),
    sendWhatsAppText: builder.mutation({
      query: ({ enquiryId, ...body }) => ({ url: `/v2/whatsapp/enquiries/${enquiryId}/send-text`, method: 'POST', body }),
      invalidatesTags: (r, e, { enquiryId }) => [{ type: 'EnquiryContactLogs', id: enquiryId }, 'Enquiries'],
    }),
    getUnmatchedWhatsAppInbound: builder.query({
      query: ({ limit = 20, skip = 0 } = {}) => ({ url: '/v2/whatsapp/inbound/unmatched', params: { limit, skip } }),
      providesTags: ['WhatsAppInbound'],
    }),
    linkWhatsAppInbound: builder.mutation({
      query: ({ inboundId, enquiryId }) => ({ url: `/v2/whatsapp/inbound/${inboundId}/link`, method: 'POST', body: { enquiry_id: enquiryId } }),
      invalidatesTags: (r, e, { enquiryId }) => ['WhatsAppInbound', 'Enquiries', { type: 'EnquiryContactLogs', id: enquiryId }],
    }),
    ignoreWhatsAppInbound: builder.mutation({
      query: (inboundId) => ({ url: `/v2/whatsapp/inbound/${inboundId}/ignore`, method: 'POST' }),
      invalidatesTags: ['WhatsAppInbound'],
    }),
  }),
});

export const {
  // Auth
  useRegisterMutation,
  useCompletePartnerOnboardingMutation,
  useLoginMutation,
  useGetMeQuery,
  useUpdateMeMutation,
  // Listings
  useGetListingsQuery,
  useGetListingsPreviewQuery,
  useGetListingQuery,
  useGetListingAvailabilityQuery,
  useLazyGetListingQuery,
  useGetSavedListingsQuery,
  useSaveListingMutation,
  useRemoveSavedListingMutation,
  useGetNearbyPlacesForListingQuery,
  // Enquiries v1 legacy
  useCreateEnquiryMutation,
  useGetUserEnquiriesQuery,
  useGetEnquiryQuery,
  useSubmitBookingIdConfirmationMutation,
  useSubmitBookingIdVerificationMutation,
  usePartnerSubmitBookingIdMutation,
  useAdminConfirmBookingManuallyMutation,
  // Config v2
  useGetExchangeRatesQuery,
  useRefreshExchangeRatesMutation,
  // Organizations v2
  useGetMyOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useListOrganizationsQuery,
  // Enquiries v2
  useCreateEnquiryV2Mutation,
  useGetMyEnquiriesV2Query,
  useGetEnquiryV2Query,
  useGetEnquiryWorkflowQuery,
  useCancelEnquiryV2Mutation,
  useListAllEnquiriesV2Query,
  useQualifyEnquiryMutation,
  useMatchEnquiryMutation,
  // Offers v2
  useGetMyOfferRequestsQuery,
  useConfirmOfferMutation,
  useDeclineOfferMutation,
  useCreateOfferForEnquiryMutation,
  // Quotes v2
  useCreateQuoteMutation,
  useSendQuoteMutation,
  useGetQuoteQuery,
  useGetMyQuotesQuery,
  useAcceptQuoteMutation,
  useDeclineQuoteMutation,
  // Bookings v2
  useGetMyBookingsQuery,
  useGetBookingQuery,
  useVerifyPaymentMutation,
  useReportPaymentMutation,
  useConfirmBookingMutation,
  useResendBookingEmailMutation,
  useAdminListBookingsQuery,
  useAdminListBookingSummariesQuery,
  useAdminGetBookingDetailQuery,
  // Stays v2
  useGetMyStaysQuery,
  useGetStayQuery,
  useCheckInMutation,
  useStartStayMutation,
  useCheckOutMutation,
  useMarkNoShowMutation,
  useCompleteStayMutation,
  useCreateSupportCaseMutation,
  useCreatePublicContactMutation,
  useAdminListSupportCasesQuery,
  useAdminTransitionSupportCaseMutation,
  useAdminListStaysQuery,
  useAdminListStaySummariesQuery,
  useAdminGetStayDetailQuery,
  useAdminListExtensionRequestsQuery,
  useAdminGetExtensionRequestQuery,
  useLazyAdminGetEligibleExtensionInvitationTargetsQuery,
  useAdminSendExtensionInvitationMutation,
  useAdminRequestExtensionAvailabilityMutation,
  useAdminQuoteExtensionMutation,
  useAdminAcceptExtensionMutation,
  useAdminRejectExtensionMutation,
  useAdminCancelExtensionMutation,
  useAdminCreateExtensionInvoiceMutation,
  // Rewards v2
  useGetMyRewardsQuery,
  useCreateRewardMutation,
  useApproveRewardMutation,
  useRejectRewardMutation,
  // Reviews v2
  useCreateReviewMutation,
  useGetReviewForStayQuery,
  useListReviewsForListingQuery,
  useGetExternalReviewsForListingQuery,
  // Documents v2
  useGetUploadUrlMutation,
  useRegisterDocumentMutation,
  useGetDocumentsQuery,
  // Partner Policy
  useGetMyPolicyQuery,
  useSaveMyPolicyMutation,
  useSubmitPolicyForReviewMutation,
  useAdminGetPartnerPolicyQuery,
  useAdminApprovePolicyMutation,
  useAdminRejectPolicyMutation,
  useGetListingPartnerPolicyQuery,
  // Partner Chat
  useGetPartnerChatQuery,
  useSendChatMessageMutation,
  useMarkChatReadMutation,
  useClearChatMutation,
  useEditChatMessageMutation,
  // Partner Invoices
  useGetPartnerInvoicesQuery,
  // Partner property policies
  useGetMyPropertyPoliciesQuery,
  useSaveMyPropertyPoliciesMutation,
  useGetPropertyPoliciesQuery,
  useParsePropertyRulesMutation,
  useSavePropertyPoliciesMutation,
  useGetResolvedPoliciesQuery,
  // Notification Config
  useGetNotificationConfigQuery,
  useUpdateNotificationConfigMutation,
  useGetManualNotificationRecipientsQuery,
  useSendManualNotificationMutation,
  useGetNotificationTemplatesQuery,
  useGetNotificationTemplateQuery,
  useCreateCustomNotificationTemplateEventMutation,
  useSaveNotificationTemplateMutation,
  usePreviewNotificationTemplateMutation,
  useTestNotificationTemplateMutation,
  useGetNotificationTemplateHistoryQuery,
  useGetEmailProviderStatusQuery,
  useTestEmailProviderMutation,
  useGetEmailDeliveriesQuery,
  useRetryEmailDeliveryMutation,
  useGetAdminInvitationsQuery,
  useCreateAdminInvitationMutation,
  useRevokeAdminInvitationMutation,
  useGetAdministratorsQuery,
  useUpdateAdministratorAccessMutation,
  useRevokeAdministratorAccessMutation,
  useVerifyAdminInvitationMutation,
  useCompleteAdminInvitationMutation,
  // Admin enquiry intake / WhatsApp
  useAdminCreateEnquiryMutation,
  useAdminLookupEnquiriesByPhoneQuery,
  useLazyAdminLookupEnquiriesByPhoneQuery,
  useGetEnquiryContactLogsQuery,
  useAddEnquiryContactLogMutation,
  useGetWhatsAppStatusQuery,
  useSendWhatsAppTemplateMutation,
  useSendWhatsAppTextMutation,
  useGetUnmatchedWhatsAppInboundQuery,
  useLinkWhatsAppInboundMutation,
  useIgnoreWhatsAppInboundMutation,
} = api;
